import { useStoredErrorText, type StoredErrorText } from "@/core/api";
import { useAuth } from "@/core/auth/hooks";
import { cn } from "@/lib/utils";
import { useConfidenceReportVisibility } from "@/modules/analytics/hooks/useConfidenceReportVisibility";
import {
  isReportActive,
  useCancelConfidenceReportMutation,
  useConfidenceReport,
  useRunConfidenceReportMutation,
} from "@/modules/analytics/services/confidenceReportApi";
import { useGetModelsQuery } from "@/modules/model/services/modelApi";
import { useActiveProject } from "@/modules/project/hooks/useActiveProject";
import { Button } from "@/modules/shadcn/ui/button";
import { Checkbox } from "@/modules/shadcn/ui/checkbox";
import { NumberInput } from "@/modules/shadcn/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/modules/shadcn/ui/tabs";
import {
  ConfidenceReportGtGeometryEnum,
  ConfidenceReportStatusEnum,
  Model,
  isOnnxInferableBackend,
  ModelOutputTypeEnum,
  ModelStatusEnum,
  OrgMemberRoleEnum,
  ProjectTypeEnum,
} from "@repo/schema";
import { AlertCircle, Loader2, Play, Square } from "lucide-react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { metricColor } from "@/modules/analytics/utils/metricColor";
import { ConfidenceBoxPlot } from "./ConfidenceBoxPlot";
import { EmptyState } from "./EmptyState";
import { formatDate, tKey, type I18nKey } from "@/core/i18n";

/** The ground-truth geometry toggle, in display order. */
const GT_GEOMETRY_OPTIONS: [ConfidenceReportGtGeometryEnum, I18nKey<"analytics">][] = [
  [ConfidenceReportGtGeometryEnum.RECTANGLE, tKey("analytics:report.gtGeometry.RECTANGLE")],
  [ConfidenceReportGtGeometryEnum.POLYGON, tKey("analytics:report.gtGeometry.POLYGON")],
];

/** Models eligible for confidence report: DONE, ONNX-inferable backend (see isOnnxInferableBackend), has RAW output, not classification. */
function isEligibleModel(model: Model): boolean {
  return (
    model.status === ModelStatusEnum.DONE &&
    isOnnxInferableBackend(model.backend) &&
    model.outputs.some((o) => o.type === ModelOutputTypeEnum.RAW) &&
    model.trainingType !== ProjectTypeEnum.CLASSIFICATION
  );
}

export const ConfidenceReportSection = () => {
  const { t } = useTranslation(["analytics", "common"]);
  const [activeProject] = useActiveProject();
  const projectId = activeProject?.id;

  const { role } = useAuth();
  const canManage =
    role === OrgMemberRoleEnum.ADMIN || role === OrgMemberRoleEnum.OWNER;

  // ── data ──────────────────────────────────────────────────────────────────
  const { data: models = [] } = useGetModelsQuery(
    { projectId: projectId! },
    { skip: !projectId || !canManage },
  );

  const eligibleModels = models
    .filter(isEligibleModel)
    .toSorted((a, b) => b.createdAt.localeCompare(a.createdAt));

  const { data: report } = useConfidenceReport(projectId);

  const { showInDataset, setShowInDataset } = useConfidenceReportVisibility();

  const [runReport, { isLoading: isRunning }] =
    useRunConfidenceReportMutation();
  const [cancelReport, { isLoading: isCancelling }] =
    useCancelConfidenceReportMutation();

  // ── local state ────────────────────────────────────────────────────────────
  const defaultModelId = eligibleModels[0]?.id;
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [conf, setConf] = useState<number | null>(0.25);
  const [matchIou, setMatchIou] = useState<number | null>(0.5);
  const [gtGeometry, setGtGeometry] = useState<ConfidenceReportGtGeometryEnum>(
    ConfidenceReportGtGeometryEnum.RECTANGLE,
  );

  const effectiveModelId = selectedModelId ?? defaultModelId ?? null;
  const selectedModel = models.find((m) => m.id === effectiveModelId);
  const isDetection = selectedModel?.trainingType === ProjectTypeEnum.DETECTION;

  const active = isReportActive(report);

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleRun = async () => {
    if (
      !projectId ||
      effectiveModelId == null ||
      conf == null ||
      matchIou == null
    )
      return;
    try {
      await runReport({
        projectId,
        modelId: effectiveModelId,
        conf,
        matchIou,
        gtGeometry: isDetection
          ? gtGeometry
          : ConfidenceReportGtGeometryEnum.POLYGON,
      }).unwrap();
    } catch {
      // error surfaced via report.status === ERROR
    }
  };

  const handleCancel = async () => {
    if (!projectId) return;
    try {
      await cancelReport({ projectId }).unwrap();
    } catch {
      // ignore
    }
  };

  if (!projectId) return null;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4 border-t border-black/10 pt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">
          {t("report.title")}
        </p>
        {/* Display preference, not a run parameter — always visible and
            enabled, applies immediately without re-running the report. */}
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={showInDataset}
            onCheckedChange={(value) => setShowInDataset(value === true)}
          />
          {t("report.showInDataset")}
        </label>
      </div>

      {/* Controls row — running reports is restricted to admins and owners */}
      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          {/* Model selector */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">{t("report.model")}</span>
            <Select
              value={effectiveModelId != null ? String(effectiveModelId) : ""}
              onValueChange={(v) => {
                if (v !== null) setSelectedModelId(Number(v));
              }}
              disabled={active || eligibleModels.length === 0}
            >
              <SelectTrigger size="sm" className="w-48">
                <SelectValue
                  placeholder={
                    eligibleModels.length === 0
                      ? t("report.noEligibleModels")
                      : t("report.selectModel")
                  }
                >
                  {selectedModel?.name ?? eligibleModels[0]?.name}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {eligibleModels.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Confidence threshold */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">
              {t("report.confidence")}
            </span>
            <NumberInput
              value={conf}
              onValueChange={(v) => setConf(v == null ? null : Math.min(1, v))}
              decimal
              min={0}
              max={1}
              step={0.01}
              className="h-9 w-24 text-sm"
              disabled={active}
            />
          </div>

          {/* IoU matching threshold — a prediction counts as a TP at IoU ≥ this */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">IoU</span>
            <NumberInput
              value={matchIou}
              onValueChange={(v) =>
                setMatchIou(v == null ? null : Math.min(1, v))
              }
              decimal
              min={0}
              max={1}
              step={0.01}
              className="h-9 w-24 text-sm"
              disabled={active}
            />
          </div>

          {/* GT geometry toggle — detection models only */}
          {isDetection && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                {t("report.groundTruth")}
              </span>
              <div className="flex gap-1">
                {GT_GEOMETRY_OPTIONS.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    disabled={active}
                    onClick={() => setGtGeometry(value)}
                    className={cn(
                      "h-9 cursor-pointer rounded-md border px-3 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50",
                      gtGeometry === value
                        ? "border-primary/30 bg-primary/10 text-primary"
                        : "border-black/10 bg-transparent text-muted-foreground hover:bg-black/4",
                    )}
                  >
                    {t(label)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Run / Cancel button — label spacer keeps it bottom-aligned with labelled inputs */}
          <div className="ml-auto flex flex-col gap-1">
            <span className="invisible text-xs">_</span>
            <div className="flex items-center gap-2">
              {active && report && (
                <span className="text-xs text-muted-foreground">
                  {t("report.progress", {
                    processed: report.processed,
                    count: report.total,
                  })}
                </span>
              )}
              {active ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                  ) : (
                    <Square className="mr-1.5 size-3.5" />
                  )}
                  {t("common:actions.cancel")}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleRun}
                  disabled={
                    isRunning ||
                    eligibleModels.length === 0 ||
                    conf == null ||
                    matchIou == null
                  }
                >
                  {isRunning ? (
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                  ) : (
                    <Play className="mr-1.5 size-3.5" />
                  )}
                  {t("report.run")}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Results area */}
      <ReportResults report={report} active={active} canManage={canManage} />
    </div>
  );
};

// ─── Results sub-component ────────────────────────────────────────────────────

type Report = NonNullable<ReturnType<typeof useConfidenceReport>["data"]>;

/** The failed report's headline, with the raw job error under it when it adds anything. */
const ReportFailure = ({ failure }: { failure: StoredErrorText | null }) => {
  const { t } = useTranslation(["analytics"]);
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span>{failure?.headline ?? t("report.unknownError")}</span>
      {failure?.details && (
        <span className="text-xs break-words text-red-600/70">{failure.details}</span>
      )}
    </div>
  );
};

const ReportResults = ({
  report,
  active,
  canManage,
}: {
  report: Report | undefined;
  active: boolean;
  canManage: boolean;
}) => {
  const { t } = useTranslation(["analytics", "common"]);
  const storedErrorText = useStoredErrorText();
  if (!report) {
    return (
      <EmptyState
        message={
          canManage ? t("report.emptyCanManage") : t("report.emptyReadOnly")
        }
      />
    );
  }

  if (report.status === ConfidenceReportStatusEnum.PENDING || active) {
    return (
      <div className="flex h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-black/10 text-sm text-muted-foreground">
        <Loader2 className="size-5 animate-spin opacity-50" />
        <span>
          {report.status === ConfidenceReportStatusEnum.PENDING
            ? t("report.starting")
            : t("report.processing", {
                processed: report.processed,
                count: report.total,
              })}
        </span>
      </div>
    );
  }

  if (report.status === ConfidenceReportStatusEnum.ERROR) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
        <AlertCircle className="size-4 shrink-0" />
        <ReportFailure failure={storedErrorText(report)} />
      </div>
    );
  }

  if (report.status === ConfidenceReportStatusEnum.CANCELLED) {
    return (
      <EmptyState
        message={
          canManage ? t("report.cancelledCanManage") : t("report.cancelled")
        }
      />
    );
  }

  if (!report.perClassStats || report.perClassStats.length === 0) {
    return (
      <EmptyState message={t("report.noPerClassStats")} />
    );
  }

  const caption = [
    report.modelName && t("report.caption.model", { name: report.modelName }),
    t("report.caption.threshold", { value: (report.conf * 100).toFixed(0) }),
    t("report.caption.iou", { value: report.matchIou }),
    t("report.caption.ran", { date: formatDate(report.createdAt) }),
  ]
    .filter(Boolean)
    .join(" · ");

  const fmtPct = (v: number | null | undefined) =>
    v == null ? "-" : `${(v * 100).toFixed(0)}%`;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">{caption}</p>
      <p className="text-xs text-muted-foreground">
        <Trans
          t={t}
          i18nKey="report.overall"
          values={{
            precision: fmtPct(report.overallPrecision),
            recall: fmtPct(report.overallRecall),
          }}
          components={{
            precision: (
              <span
                className={cn(
                  report.overallPrecision != null
                    ? metricColor(report.overallPrecision)
                    : "text-muted-foreground",
                )}
              />
            ),
            recall: (
              <span
                className={cn(
                  report.overallRecall != null
                    ? metricColor(report.overallRecall)
                    : "text-muted-foreground",
                )}
              />
            ),
          }}
        />
      </p>

      <Tabs defaultValue="confidence">
        <TabsList variant="line" className="shrink-0">
          <TabsTrigger value="confidence">{t("report.confidence")}</TabsTrigger>
          <TabsTrigger value="iou">IoU</TabsTrigger>
        </TabsList>

        <TabsContent value="confidence" className="pt-4">
          <p className="mb-3 text-xs text-muted-foreground">
            {t("report.confidenceCaption")}
          </p>
          <ConfidenceBoxPlot
            stats={report.perClassStats}
            metric="confidence"
            yAxisLabel={t("report.confidence")}
          />
        </TabsContent>

        <TabsContent value="iou" className="pt-4">
          <p className="mb-3 text-xs text-muted-foreground">
            {t("report.iouCaption", { value: report.matchIou })}
          </p>
          <ConfidenceBoxPlot
            stats={report.perClassStats}
            metric="iou"
            yAxisLabel="IoU"
            matchIou={report.matchIou}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
